'use client';

import { c as _c } from "react/compiler-runtime";
import { useRouter } from 'next/navigation';
import { createContext, useContext, useState } from 'react';
import { completeOnboarding } from '../actions';
import type { OnboardingFlow, OnboardingStep } from '../config';
interface OnboardingState {
  flow: OnboardingFlow;
  currentStepIndex: number;
  isComplete: boolean;
  redirectUrl: string | null;
}
interface OnboardingContextValue extends OnboardingState {
  currentStep: OnboardingStep | null;
  canGoBack: boolean;
  isLastStep: boolean;
  totalSteps: number;
  next: () => void;
  back: () => void;
  skip: () => void;
  complete: () => Promise<void>;
}
const OnboardingContext = createContext<OnboardingContextValue | null>(null);
interface OnboardingProviderProps {
  flow: OnboardingFlow;
  redirectUrl?: string | null;
  children: React.ReactNode;
}
export function OnboardingProvider(t0) {
  const $ = _c(21);
  const {
    flow,
    redirectUrl: t1,
    children
  } = t0;
  const redirectUrl = t1 === undefined ? null : t1;
  useRouter();
  let t2;
  if ($[0] !== flow || $[1] !== redirectUrl) {
    t2 = {
      flow,
      currentStepIndex: 0,
      isComplete: false,
      redirectUrl
    };
    $[0] = flow;
    $[1] = redirectUrl;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [state, setState] = useState(t2);
  const currentStep = state.flow.steps[state.currentStepIndex] ?? null;
  const canGoBack = state.currentStepIndex > 0;
  const isLastStep = state.currentStepIndex === state.flow.steps.length - 1;
  const totalSteps = state.flow.steps.length;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setState(_temp);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const next = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setState(_temp2);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const back = t4;
  let t5;
  if ($[5] !== state.currentStepIndex || $[6] !== state.flow.steps) {
    t5 = () => {
      const step = state.flow.steps[state.currentStepIndex];
      if (step?.skippable) {
        next();
      }
    };
    $[5] = state.currentStepIndex;
    $[6] = state.flow.steps;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const skip = t5;
  let t6;
  if ($[8] !== state.redirectUrl) {
    t6 = async () => {
      await completeOnboarding(state.redirectUrl);
      setState(_temp3);
    };
    $[8] = state.redirectUrl;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const complete = t6;
  let t7;
  if ($[10] !== canGoBack || $[11] !== complete || $[12] !== currentStep || $[13] !== isLastStep || $[14] !== skip || $[15] !== state || $[16] !== totalSteps) {
    t7 = {
      ...state,
      currentStep,
      canGoBack,
      isLastStep,
      totalSteps,
      next,
      back,
      skip,
      complete
    };
    $[10] = canGoBack;
    $[11] = complete;
    $[12] = currentStep;
    $[13] = isLastStep;
    $[14] = skip;
    $[15] = state;
    $[16] = totalSteps;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const value = t7;
  let t8;
  if ($[18] !== children || $[19] !== value) {
    t8 = <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
    $[18] = children;
    $[19] = value;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function _temp3(prev_1) {
  return {
    ...prev_1,
    isComplete: true
  };
}
function _temp2(prev_0) {
  if (prev_0.currentStepIndex > 0) {
    return {
      ...prev_0,
      currentStepIndex: prev_0.currentStepIndex - 1
    };
  }
  return prev_0;
}
function _temp(prev) {
  if (prev.currentStepIndex < prev.flow.steps.length - 1) {
    return {
      ...prev,
      currentStepIndex: prev.currentStepIndex + 1
    };
  }
  return prev;
}
export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
}